import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  OBTURATEUR_TYPES, ATTENUATIONS_ER, FILTRE_ER_IMAGES,
  ATTENUATIONS_ELACIN, FILTRE_ELACIN_IMAGES,
} from './orderFormConstants';
import { OrangeInfoBox, MatiereSelect, DureteSelect, CouleurSelect } from './OrderFormHelpers';

export default function FormObturateurSection({ form, compact, idPrefix }) {
  const id = (name) => idPrefix ? `${idPrefix}-${name}` : name;
  const labelClass = compact ? 'label-overline mb-1 block' : 'label-overline mb-2 block';
  const sectionClass = compact
    ? 'bg-white border border-zinc-200 p-3 sm:p-4 rounded-sm'
    : 'bg-white border border-zinc-200 p-4 sm:p-6';
  const headingClass = compact
    ? 'font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200'
    : 'font-heading font-semibold text-base sm:text-lg mb-4 pb-2 border-b border-zinc-200';
  const gapClass = compact ? 'gap-3' : 'gap-4';
  const H = compact ? 'h3' : 'h2';

  const isAntiEau = form.obturateurType === 'anti_eau';
  // Un anti-eau flottant est soit une commande historique enregistrée sous
  // l'ancien type 'anti_eau_flottant', soit une commande courante avec la
  // case « Flottant » cochée pour un anti_eau.
  const isAntiEauFlottant = form.obturateurType === 'anti_eau_flottant' || (isAntiEau && form.obturateurFlottant);
  const isAntiEauFamily = isAntiEau || isAntiEauFlottant;
  const isAntiBruitNF = form.obturateurType === 'anti_bruit_non_filtre';
  const isAntiBruitF = form.obturateurType === 'anti_bruit_filtre';
  const isBouchonMusique = form.obturateurType === 'bouchon_musique';
  const isFilteredType = isAntiBruitF || isBouchonMusique;

  if (form.fabricationType !== 'obturateur' || !form.obturateurType) return null;

  return (
    <div className={sectionClass} data-testid="obturateur-options-section">
      <H className={headingClass}>
        {form.obturateurType === 'anti_eau_flottant'
          ? 'Anti-Eau flottant'
          : (OBTURATEUR_TYPES.find(t => t.value === form.obturateurType)?.label || 'Options')}
      </H>

      {isFilteredType && (
        <OrangeInfoBox>
          Les filtres permettent différentes atténuations. Merci de choisir l&apos;atténuation souhaitée.
        </OrangeInfoBox>
      )}

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${gapClass} mt-4`}>
        {/* Dureté */}
        <div>
          <Label className={labelClass}>Dureté (Shore) *</Label>
          {isFilteredType ? (
            form.matiere === 'resine_souple'
              ? <DureteSelect matiere="resine_souple" value={form.durete} onChange={form.setDurete} testId="obturateur-durete" />
              : <div className="h-10 flex items-center px-3 border border-zinc-200 rounded-sm bg-zinc-50 text-sm text-zinc-700">{form.matiere === 'resine_3d' ? '—' : '40 SH'}</div>
          ) : isAntiEauFamily ? (
            // Anti-Eau : dureté forcée par la case Flottant. 35 SH si flottant, 40 SH sinon.
            <div className="h-10 flex items-center px-3 border border-zinc-200 rounded-sm bg-zinc-50 text-sm text-zinc-700" data-testid="obturateur-durete-locked">
              {isAntiEauFlottant ? '35 SH' : '40 SH'}
            </div>
          ) : (
            <DureteSelect matiere="silicone" value={form.durete} onChange={form.setDurete} testId="obturateur-durete" />
          )}
        </div>

        {/* Couleur */}
        <div>
          <Label className={labelClass}>Couleur *</Label>
          {isFilteredType ? (
            <div>
              <div className="h-10 flex items-center px-3 border border-zinc-200 rounded-sm bg-zinc-50 text-sm text-zinc-700">Transparent</div>
              <p className="text-xs text-zinc-500 mt-1">Rouge = droite, Bleu = gauche (indication)</p>
            </div>
          ) : (
            /* Matrice couleur × dureté. Contexte "paired-only" (Anti-Eau ou 40 SH) :
               rouge/bleu unis remplacés par l'appairé Rouge (D) / Bleu (G). */
            <CouleurSelect
              shores={[form.durete]}
              value={form.couleur}
              onChange={form.setCouleur}
              exclude={(isAntiEauFamily || form.durete === '40') ? ['bleu_rouge', 'rouge', 'bleu'] : ['bleu_rouge', 'rouge_bleu']}
              testId="obturateur-couleur"
            />
          )}
        </div>

        {/* Matière (filtered types only) */}
        {isFilteredType && (
          <div>
            <Label className={labelClass}>Matière *</Label>
            <MatiereSelect value={form.matiere} onChange={form.setMatiere} testId="obturateur-matiere" />
          </div>
        )}

        {/* Atténuation / Filtre */}
        {isFilteredType && (
          <div>
            <Label className={labelClass}>{isAntiBruitF ? 'Filtre Elacin *' : 'Filtre ER *'}</Label>
            <Select value={form.obturateurAttenuation} onValueChange={form.setObturateurAttenuation}>
              <SelectTrigger className="rounded-sm bg-white" data-testid="obturateur-attenuation"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
              <SelectContent className="bg-white">
                {(isAntiBruitF ? ATTENUATIONS_ELACIN : ATTENUATIONS_ER).map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Image filtre Elacin */}
        {isAntiBruitF && form.obturateurAttenuation && FILTRE_ELACIN_IMAGES[form.obturateurAttenuation] && (
          <div className="sm:col-span-2 flex items-center gap-3">
            <div className="w-24 h-24 rounded-sm overflow-hidden border border-zinc-200 bg-white flex-shrink-0" data-testid="filtre-elacin-img">
              <img src={FILTRE_ELACIN_IMAGES[form.obturateurAttenuation]} alt={form.obturateurAttenuation} className="w-full h-full object-contain" />
            </div>
            <p className="text-sm text-zinc-600">{ATTENUATIONS_ELACIN.find(a => a.value === form.obturateurAttenuation)?.label}</p>
          </div>
        )}

        {/* Image filtre ER */}
        {isBouchonMusique && form.obturateurAttenuation && FILTRE_ER_IMAGES[form.obturateurAttenuation] && (
          <div className="sm:col-span-2 flex items-center gap-3">
            <div className="w-24 h-24 rounded-sm overflow-hidden border border-zinc-200 bg-white flex-shrink-0" data-testid="filtre-er-img">
              <img src={FILTRE_ER_IMAGES[form.obturateurAttenuation]} alt={form.obturateurAttenuation} className="w-full h-full object-contain" />
            </div>
            <p className="text-sm text-zinc-600">{ATTENUATIONS_ER.find(a => a.value === form.obturateurAttenuation)?.label}</p>
          </div>
        )}

        {/* Forme: bouchon musique */}
        {isBouchonMusique && (
          <div className="sm:col-span-2">
            <Label className={labelClass}>Forme *</Label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-testid="bouchon-musique-thumbnails">
              {[
                { value: 'canule', label: 'Canule', img: '/images/formes/filtre_canule.png' },
                { value: 'fond_conque', label: 'Fond de conque', img: '/images/formes/filtre_fond_conque.png' },
              ].map((thumb) => {
                const active = form.obturateurForme === thumb.value;
                return (
                  <button
                    type="button"
                    key={thumb.value}
                    onClick={() => form.setObturateurForme(thumb.value)}
                    className={`relative flex flex-col items-center p-2 rounded-sm border-2 transition-colors bg-white ${active ? 'border-zinc-900 ring-2 ring-zinc-900/10 shadow-sm' : 'border-zinc-200 hover:border-zinc-400'}`}
                    data-testid={`bouchon-thumb-${thumb.value}`}>
                    <div className="w-full aspect-square bg-zinc-50 rounded-sm overflow-hidden flex items-center justify-center">
                      <img src={thumb.img} alt={thumb.label} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                    <p className={`text-[11px] mt-1.5 text-center leading-tight ${active ? 'font-semibold text-zinc-900' : 'text-zinc-700'}`}>{thumb.label}</p>
                    {active && <span className="absolute top-1 right-1 bg-zinc-900 text-white text-[10px] font-bold rounded-sm px-1.5 py-0.5">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Forme: anti-bruit filtré (mêmes 2 modèles que bouchons musique) */}
        {isAntiBruitF && (
          <div className="sm:col-span-2">
            <Label className={labelClass}>Forme *</Label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-testid="anbf-forme-thumbnails">
              {[
                { value: 'canule', label: 'Canule', img: '/images/formes/filtre_canule.png' },
                { value: 'fond_conque', label: 'Fond de conque', img: '/images/formes/filtre_fond_conque.png' },
              ].map((thumb) => {
                const active = form.obturateurForme === thumb.value;
                return (
                  <button
                    type="button"
                    key={thumb.value}
                    onClick={() => form.setObturateurForme(thumb.value)}
                    className={`relative flex flex-col items-center p-2 rounded-sm border-2 transition-colors bg-white ${active ? 'border-zinc-900 ring-2 ring-zinc-900/10 shadow-sm' : 'border-zinc-200 hover:border-zinc-400'}`}
                    data-testid={`anbf-thumb-${thumb.value}`}>
                    <div className="w-full aspect-square bg-zinc-50 rounded-sm overflow-hidden flex items-center justify-center">
                      <img src={thumb.img} alt={thumb.label} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                    <p className={`text-[11px] mt-1.5 text-center leading-tight ${active ? 'font-semibold text-zinc-900' : 'text-zinc-700'}`}>{thumb.label}</p>
                    {active && <span className="absolute top-1 right-1 bg-zinc-900 text-white text-[10px] font-bold rounded-sm px-1.5 py-0.5">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Forme: anti-bruit non filtré */}
        {isAntiBruitNF && (
          <div className="sm:col-span-2">
            <Label className={labelClass}>Forme *</Label>
            <Select value={form.obturateurForme} onValueChange={form.setObturateurForme}>
              <SelectTrigger className="rounded-sm bg-white" data-testid="obturateur-forme-anbf-select"><SelectValue placeholder="Sélectionner une forme" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="solide_plug_poignee">Solide plug + poignée</SelectItem>
                <SelectItem value="fond_conque_confort">Fond de conque confort</SelectItem>
                <SelectItem value="fond_conduit_discret">Fond de conduit discret</SelectItem>
              </SelectContent>
            </Select>

            {/* Illustrative thumbnails */}
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid="anbf-forme-thumbnails">
              {[
                { value: 'solide_plug_poignee', label: 'Solide plug + poignée', img: '/images/formes/obturateur_poignee.png' },
                { value: 'fond_conque_confort', label: 'Fond de conque confort', img: '/images/formes/antibruit_non_filtre.png' },
                { value: 'fond_conduit_discret', label: 'Fond de conduit discret', img: '/images/formes/obturateur_fond_conduit_discret.png' },
              ].map((thumb) => {
                const active = form.obturateurForme === thumb.value;
                return (
                  <button
                    type="button"
                    key={thumb.value}
                    onClick={() => form.setObturateurForme(thumb.value)}
                    className={`flex flex-col items-center p-2 rounded-sm border transition-colors ${active ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`}
                    data-testid={`anbf-thumb-${thumb.value}`}>
                    <div className="w-full h-20 sm:h-24 bg-white rounded-sm overflow-hidden flex-shrink-0">
                      <img src={thumb.img} alt={thumb.label} className="w-full h-full object-contain" />
                    </div>
                    <p className={`text-[11px] mt-1.5 text-center leading-tight ${active ? 'font-semibold text-zinc-900' : 'text-zinc-600'}`}>{thumb.label}</p>
                  </button>
                );
              })}
            </div>

            {form.obturateurForme === 'solide_plug_poignee' && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-sm p-2.5 flex items-start gap-2" data-testid="anbf-forme-warning">
                <span className="text-amber-700 text-base leading-none">⚠️</span>
                <p className="text-xs text-amber-800"><strong>Ne convient pas pour le sommeil.</strong></p>
              </div>
            )}
            {form.obturateurForme === 'fond_conque_confort' && (
              <p className="mt-2 text-xs text-zinc-600 italic" data-testid="anbf-forme-info">
                Convient par exemple pour la nuit ou le travail.
              </p>
            )}
            {form.obturateurForme === 'fond_conduit_discret' && (
              <p className="mt-2 text-xs text-zinc-600 italic" data-testid="anbf-forme-info">
                Convient aux situations peu bruyantes : permet d&apos;atténuer l&apos;ambiance sonore tout en restant disponible.
              </p>
            )}
          </div>
        )}

        {/* Flottant : sous-option de l'Anti-Eau. Coché ⇒ 35 SH, sinon 40 SH.
            (Uniquement affichée pour anti_eau — les commandes historiques
             stockées en anti_eau_flottant conservent leur état verrouillé.) */}
        {isAntiEau && (
          <div className="sm:col-span-2">
            <label className={`flex items-start gap-3 p-2.5 sm:p-3 rounded-sm border cursor-pointer transition-colors w-full ${form.obturateurFlottant ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="obturateur-flottant-checkbox">
              <Checkbox checked={form.obturateurFlottant} onCheckedChange={(v) => form.setObturateurFlottant(!!v)} />
              <div>
                <span className="font-medium text-sm">Flottant</span>
                <p className="text-xs text-zinc-500 mt-0.5">Si coché, dureté forcée à 35 SH. Sinon 40 SH.</p>
              </div>
            </label>
          </div>
        )}

        {/* Poignée */}
        {(isAntiEauFamily || isAntiBruitF || isBouchonMusique) && (
          <div>
            <Label className={labelClass}>Poignée</Label>
            <RadioGroup value={form.obturateurPoignee ? 'oui' : 'non'} onValueChange={(v) => form.setObturateurPoignee(v === 'oui')} className="flex gap-4 mt-1">
              <div className="flex items-center space-x-2"><RadioGroupItem value="oui" id={id('poignee-oui')} data-testid="poignee-oui" /><Label htmlFor={id('poignee-oui')} className="cursor-pointer text-sm">Oui</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="non" id={id('poignee-non')} data-testid="poignee-non" /><Label htmlFor={id('poignee-non')} className="cursor-pointer text-sm">Non</Label></div>
            </RadioGroup>
          </div>
        )}

        {/* Cordon de sécurité */}
        <div>
          <Label className={labelClass}>Cordon de sécurité</Label>
          <RadioGroup value={form.obturateurCordon ? 'oui' : 'non'} onValueChange={(v) => form.setObturateurCordon(v === 'oui')} className="flex gap-4 mt-1">
            <div className="flex items-center space-x-2"><RadioGroupItem value="oui" id={id('cordon-oui')} data-testid="cordon-oui" /><Label htmlFor={id('cordon-oui')} className="cursor-pointer text-sm">Oui</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="non" id={id('cordon-non')} data-testid="cordon-non" /><Label htmlFor={id('cordon-non')} className="cursor-pointer text-sm">Non</Label></div>
          </RadioGroup>
        </div>

        {/* Étui */}
        <div>
          <Label className={labelClass}>Étui</Label>
          <RadioGroup value={form.obturateurEtui ? 'oui' : 'non'} onValueChange={(v) => form.setObturateurEtui(v === 'oui')} className="flex gap-4 mt-1">
            <div className="flex items-center space-x-2"><RadioGroupItem value="oui" id={id('etui-oui')} data-testid="etui-oui" /><Label htmlFor={id('etui-oui')} className="cursor-pointer text-sm">Oui</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="non" id={id('etui-non')} data-testid="etui-non" /><Label htmlFor={id('etui-non')} className="cursor-pointer text-sm">Non</Label></div>
          </RadioGroup>
        </div>

        {/* Marquage spécial */}
        <div className="sm:col-span-2">
          <label className={`flex items-center gap-3 p-2.5 sm:p-3 rounded-sm border cursor-pointer transition-colors w-full ${form.marquageSpecial ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid="marquage-special-checkbox">
            <Checkbox checked={form.marquageSpecial} onCheckedChange={(v) => { form.setMarquageSpecial(v); if (!v) form.setMarquageSpecialTexte(''); }} />
            <span className="font-medium text-sm">Marquage spécial</span>
          </label>
          {form.marquageSpecial && (
            <Input
              placeholder="10 caractères max."
              value={form.marquageSpecialTexte}
              onChange={(e) => form.setMarquageSpecialTexte(e.target.value.slice(0, 10))}
              maxLength={10}
              className="rounded-sm mt-2"
              data-testid="marquage-special-input"
            />
          )}
        </div>
      </div>
    </div>
  );
}
